import { APP_LOGO, APP_NAME, COMING_SOON_ART, X_URL } from "@/lib/brand"
import { lumi } from "@/lib/lumi"
import type { SiteSetting } from "@/types"
import { useRef, useState, type FormEvent } from "react"

const ROADMAP_STEPS = [
  "Development of Klipo App",
  "Build Socials",
  "Create Telegram Group",
  "Build Community",
  "Token Launch",
  "Integrate token into Klipo",
  "Launch Klipo App",
  "More to be announced",
]

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type ComingSoonPageProps = {
  setting?: SiteSetting | null
  onSettingChange?: (setting: SiteSetting | null) => void
}

export default function ComingSoonPage(_props: ComingSoonPageProps) {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState("")
  const [error, setError] = useState("")
  const [saving, setSaving] = useState(false)
  const [roadmapOpen, setRoadmapOpen] = useState(false)
  const logoTaps = useRef(0)
  const logoTapTimer = useRef(0)

  const openAdminSignIn = () => {
    logoTaps.current += 1
    window.clearTimeout(logoTapTimer.current)
    if (logoTaps.current >= 5) {
      logoTaps.current = 0
      window.location.assign("/admin")
      return
    }
    logoTapTimer.current = window.setTimeout(() => {
      logoTaps.current = 0
    }, 1800)
  }

  const joinWaitlist = async (event: FormEvent) => {
    event.preventDefault()
    const nextEmail = email.trim().toLowerCase()
    if (!EMAIL_RE.test(nextEmail)) {
      setError("Enter a valid email address.")
      setStatus("")
      return
    }
    setSaving(true)
    setError("")
    setStatus("")
    try {
      const now = new Date().toISOString()
      await lumi.entities.waitlist_emails.create({
        email: nextEmail,
        source: "coming_soon",
        creator: "waitlist",
        createdAt: now,
        updatedAt: now,
      })
      setEmail("")
      setStatus("You're on the list. We'll send launch updates.")
    } catch (err) {
      console.error("Waitlist signup failed:", err)
      setError("Could not save your email. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col items-center px-5 py-10 sm:px-8">
        <button type="button" onClick={openAdminSignIn} className="border-0 bg-transparent p-0">
          <img src={APP_LOGO} alt={APP_NAME} className="h-16 w-auto object-contain sm:h-20" />
        </button>
        <h1 className="mt-8 text-center font-[Syne] text-3xl font-extrabold uppercase leading-tight tracking-tight sm:text-5xl">
          KLIPO TOKEN & APP LAUNCH
          <span className="mt-2 block text-[#00FF66]">IS COMING SOON</span>
        </h1>
        <p className="mt-4 max-w-xl text-center text-sm text-[#A8A8A8] sm:text-base">
          Drop your email for automatic launch updates, then follow KLIPO on X.
        </p>

        <form onSubmit={joinWaitlist} className="mt-8 w-full max-w-lg">
          <label htmlFor="waitlist-email" className="sr-only">
            Email address
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              id="waitlist-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Enter your email"
              autoComplete="email"
              className="h-12 flex-1 rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 text-sm text-white outline-none placeholder:text-[#6E6E6E] focus:border-[#00FF66]"
            />
            <button
              type="submit"
              disabled={saving}
              className="h-12 rounded-xl bg-[#00FF66] px-5 text-sm font-bold text-black disabled:opacity-60"
            >
              {saving ? "Saving..." : "Notify me"}
            </button>
          </div>
        </form>

        {status ? <p className="mt-3 text-center text-sm text-[#00FF66]">{status}</p> : null}
        {error ? <p className="mt-3 text-center text-sm text-red-400">{error}</p> : null}

        <a
          href={X_URL}
          target="_blank"
          rel="noreferrer"
          className="mt-5 flex w-full max-w-lg items-center justify-between rounded-xl border border-[#1F1F1F] bg-[#0B0B0B] px-4 py-3 transition hover:border-[#00FF66]"
        >
          <span className="text-sm font-semibold">Follow us on X for updates</span>
          <span className="text-sm font-bold text-[#00FF66]">@KLIPO_MEDIA</span>
        </a>

        <button
          type="button"
          onClick={() => setRoadmapOpen(true)}
          className="mt-8 w-full max-w-lg rounded-xl border border-[#00FF66]/40 bg-[#0B0B0B] px-4 py-3 text-sm font-bold uppercase tracking-[0.22em] text-[#00FF66] shadow-[0_0_24px_rgba(0,255,102,0.16)] transition hover:border-[#00FF66] hover:bg-[#00FF66]/10 hover:shadow-[0_0_36px_rgba(0,255,102,0.28)]"
        >
          View Roadmap
        </button>

        <img
          src={COMING_SOON_ART}
          alt="KLIPO coming soon to Robinhood Chain"
          className="mt-6 w-full max-w-xl rounded-2xl object-contain shadow-[0_0_80px_rgba(0,255,102,0.18)]"
        />
      </div>

      {roadmapOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
          onClick={() => setRoadmapOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="klipo-roadmap-title"
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-[#00FF66]/35 bg-[#050505] p-6 shadow-[0_0_60px_rgba(0,255,102,0.22)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#00FF66] to-transparent" />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#00FF66]">KLIPO</p>
                <h2 id="klipo-roadmap-title" className="mt-2 font-[Syne] text-2xl font-extrabold uppercase tracking-tight">
                  Phase 1
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setRoadmapOpen(false)}
                className="rounded-lg border border-[#1F1F1F] px-2.5 py-1 text-xs font-semibold text-[#A8A8A8] transition hover:border-[#00FF66] hover:text-[#00FF66]"
              >
                Close
              </button>
            </div>
            <ol className="mt-6 space-y-3">
              {ROADMAP_STEPS.map((step, index) => (
                <li key={step} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#00FF66]/50 bg-[#00FF66]/10 font-mono text-[11px] font-bold text-[#00FF66]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm text-white">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  )
}
